import { useSelector } from "react-redux";
import { styled } from "styled-components";

import { topicsApiSlice } from "api/topicsApi.slice";
import { SectionTitleUnderlined, SeeAllButton, StyledGrid } from "common/components";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { TopicTile } from "domains/topic/components/topicTile/TopicTile";
import type { RootState } from "types/redux";

const StyledContainer = styled.div`
  padding-bottom: 2rem;
`;

export const TopicResults = () => {
  const track = useTrack();
  const translate = useTranslate();

  const currentSearchQuery = useSelector((state: RootState) => state.search.currentSearchQuery);

  const { data: paginatedTopicSearchResults, isFetching } = topicsApiSlice.endpoints.searchTopics.useInfiniteQuery(
    {
      search: currentSearchQuery,
    },
    {
      skip: currentSearchQuery.length === 0,
    },
  );

  const topicSearchResults = paginatedTopicSearchResults?.pages.flat() ?? [];

  const onClick = (topicId: string, topicName: string) => () =>
    track({
      entityId: topicId,
      entityName: topicName,
      entityType: "Topic",
      name: "Browse page",
      page: "Search",
      subpage: "Topic detail",
    });

  if (currentSearchQuery.length === 0 || topicSearchResults.length === 0) return null;

  return (
    <StyledContainer>
      <SectionTitleUnderlined
        isLoading={isFetching}
        id="topics"
        rightElement={
          <SeeAllButton link={`/discover${currentSearchQuery.length > 0 ? `?query=${currentSearchQuery}` : ""}`} />
        }
      >
        {translate("app.discover.topics.title")}
      </SectionTitleUnderlined>
      <StyledGrid numberOfItems={[2, 3, 4, 4, 5]}>
        {topicSearchResults.slice(0, 8).map((topic) => (
          <TopicTile
            key={topic.id}
            topicId={topic.id}
            topicName={topic.name}
            provider={topic.provider}
            topicBack={{ term: "app.search.title" }}
            isCustom={topic.is_custom}
            isNew={topic.is_new}
            isFollowing={topic.is_following}
            imageUrl={topic.image_url}
            onClick={onClick(topic.id, topic.name)}
          />
        ))}
      </StyledGrid>
    </StyledContainer>
  );
};
